import { Injectable } from '@angular/core';
import { mapToVoid } from '@examdojo/core/rxjs';
import { mapQuestionBankQuestionHttpModelToStoreModel } from '@examdojo/question-bank';
import { Observable, of, tap } from 'rxjs';
import { AssignmentSectionWithQuestionsHttpModel, ClassAssignmentQuery, SaveSectionData } from '../class-assignment';
import { ClassAssignmentHttpService } from '../class-assignment/class-assignment-http.service';
import { AssignmentBuilderSectionStoreModel, AssignmentSectionType } from './assignment-builder.model';
import { AssignmentBuilderStore } from './assignment-builder.store';

@Injectable({ providedIn: 'root' })
export class AssignmentBuilderService {
  constructor(
    private readonly store: AssignmentBuilderStore,
    private readonly classAssignmentHttpService: ClassAssignmentHttpService,
    private readonly classAssignmentQuery: ClassAssignmentQuery,
  ) {}

  createSection(id: string, data: Partial<AssignmentBuilderSectionStoreModel>): void {
    this.store.create({
      id,
      type: AssignmentSectionType.Manual,
      questions: [],
      ...data,
    });
    this.store.updateProp('isDirty', true);
  }

  updateSection(id: string, data: Partial<AssignmentBuilderSectionStoreModel>): void {
    this.store.update(id, data);
    this.store.updateProp('isDirty', true);
  }

  removeSection(sectionId: string): void {
    this.store.delete(sectionId);
    this.store.updateProp('isDirty', true);
  }

  removeQuestion(sectionId: string, questionId: number): void {
    this.store.update(sectionId, (section) => ({
      ...section,
      questions: section.questions.filter((q) => q.id !== questionId),
    }));
    this.store.updateProp('isDirty', true);

    if (this.store.getValue().entities[sectionId]?.questions.length === 0) {
      this.removeSection(sectionId);
    }
  }

  reset(): void {
    this.store.reset();
  }

  init(classAssignmentId: string): Observable<void> {
    return this.classAssignmentHttpService.fetchSections(classAssignmentId).pipe(
      tap((sections) => {
        this.store.set(sections.map((section) => this.mapSectionToStoreModel(section)));
        this.store.updateProp('isDirty', false);
      }),
      mapToVoid(),
    );
  }

  saveDraft(): Observable<void> {
    if (!this.store.getValue().props.isDirty) {
      return of(undefined);
    }

    const assignmentId = this.getActiveAssignmentId();
    const sectionsData = this.getSectionsData();

    return this.classAssignmentHttpService.saveSections(assignmentId, sectionsData).pipe(
      tap(() => {
        this.store.updateProp('isDirty', false);
      }),
    );
  }

  saveDraftWithDueDate(dueAt: Date | null): Observable<void> {
    const assignmentId = this.getActiveAssignmentId();
    const sectionsData = this.getSectionsData();

    return this.classAssignmentHttpService.saveDraft(assignmentId, dueAt, sectionsData).pipe(
      tap(() => {
        this.store.updateProp('isDirty', false);
      }),
    );
  }

  private getActiveAssignmentId(): string {
    const activeAssignment = this.classAssignmentQuery.getActive();
    if (!activeAssignment?.assignment_id) {
      throw new Error('No active assignment found');
    }
    return activeAssignment.assignment_id;
  }

  private getSectionsData(): SaveSectionData[] {
    const sections = Object.values(this.store.getValue().entities);
    return sections.map((section: AssignmentBuilderSectionStoreModel, index: number) => ({
      id: section.id,
      name: section.id,
      position: index,
      questionIds: section.questions.map((q: { id: number }) => q.id),
    }));
  }

  private mapSectionToStoreModel(section: AssignmentSectionWithQuestionsHttpModel): AssignmentBuilderSectionStoreModel {
    return {
      id: section.id!,
      type: AssignmentSectionType.Manual,
      questions: section.questions.map((q) => mapQuestionBankQuestionHttpModelToStoreModel(q)),
    };
  }
}
